import InventoryAdmin from '../../components/inventory/InventoryAdmin';
import { AdminSupport } from '../../components/SupportChat';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as api from '../../api';
import { orderStatuses } from '../../data/constants';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { 
  Package, Tags, ShoppingCart, Settings, LogOut, Plus, Trash2, Edit2, 
  Download, Loader2, Users, BarChart3, Image, Upload, X 
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Data states
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  const [users, setUsers] = useState([]);
  const [banners, setBanners] = useState([]);
  const [deliveryOptions, setDeliveryOptions] = useState([]);
  const [newDelivery, setNewDelivery] = useState({ id: '', name: '', price: 0, description: '', requires_address: false, requires_city: false });
  const [pickupCode, setPickupCode] = useState('');
  const [pickupResult, setPickupResult] = useState(null);
  const [pickupError, setPickupError] = useState('');
  
  // Filters
  const [selectedOrderStatus, setSelectedOrderStatus] = useState('all');
  
  // Modals
  const [visibilityBusy, setVisibilityBusy] = useState(null);
  const [productModal, setProductModal] = useState({ open: false, data: null });
  const [promoModal, setPromoModal] = useState({ open: false, data: null });
  const [coinsModal, setCoinsModal] = useState({ open: false });
  const [bannerModal, setBannerModal] = useState({ open: false, data: null });

  // Check if already logged in
  useEffect(() => {
    const creds = localStorage.getItem('admin_credentials');
    if (creds) {
      setIsLoggedIn(true);
      loadAllData();
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    const result = await api.adminLogin(loginData.username, loginData.password);
    
    if (result.success) {
      localStorage.setItem('admin_credentials', JSON.stringify({
        username: loginData.username,
        password: loginData.password
      }));
      setIsLoggedIn(true);
      setLoginError('');
      loadAllData();
    } else {
      setLoginError(result.error);
    }
    setIsLoading(false);
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    localStorage.removeItem('admin_credentials');
    navigate('/');
  };

  const loadAllData = async () => {
    setIsLoading(true);
    try {
      const [statsData, productsData, ordersData, promosData, usersData, bannersData, deliveryData] = await Promise.all([
        api.getAdminStats(),
        api.getAdminProducts(),
        api.getAdminOrders(),
        api.getAdminPromoCodes(),
        api.getAdminUsers(),
        api.getAdminBanners(),
        api.getAdminDeliveryOptions()
      ]);
      setStats(statsData);
      setProducts(productsData);
      setOrders(ordersData);
      setPromoCodes(promosData);
      setUsers(usersData);
      setBanners(bannersData);
      setDeliveryOptions(deliveryData);
    } catch (error) {
      console.error('Failed to load data:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadOrders = async () => {
    try {
      const data = await api.getAdminOrders(selectedOrderStatus);
      setOrders(data);
    } catch (error) {
      console.error('Failed to load orders:', error);
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      loadOrders();
    }
  }, [selectedOrderStatus, isLoggedIn]);

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      await api.updateOrderStatus(orderId, newStatus);
      loadOrders();
      if (newStatus === 'delivered') {
        const newStats = await api.getAdminStats();
        setStats(newStats);
      }
    } catch (error) {
      alert('Ошибка при обновлении статуса');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Удалить товар?')) return;
    try {
      await api.deleteProduct(productId);
      setProducts(prev => prev.filter(p => p.id !== productId));
    } catch (error) {
      alert('Ошибка при удалении товара');
    }
  };

  const handleToggleProduct = async (product) => {
    if (visibilityBusy) return;
    setVisibilityBusy(product.id);
    try {
      const active = product.active === false;
      await api.updateProduct(product.id, { active });
      setProducts(prev => prev.map(item => item.id === product.id ? { ...item, active } : item));
    } catch {
      alert('Не удалось изменить видимость товара. Попробуйте ещё раз.');
    } finally { setVisibilityBusy(null); }
  };

  const handleSaveProduct = async (productData) => {
    try {
      if (productModal.data?.id) {
        await api.updateProduct(productModal.data.id, productData);
      } else {
        await api.createProduct(productData);
      }
      setProductModal({ open: false, data: null });
      const data = await api.getAdminProducts();
      setProducts(data);
    } catch (error) {
      alert('Ошибка при сохранении товара');
    }
  };

  const handleDeletePromo = async (promoId) => {
    if (!window.confirm('Удалить промокод?')) return;
    try {
      await api.deletePromoCode(promoId);
      setPromoCodes(prev => prev.filter(p => p.id !== promoId));
    } catch (error) {
      alert('Ошибка при удалении промокода');
    }
  };

  const handleSavePromo = async (promoData) => {
    try {
      await api.createPromoCode(promoData);
      setPromoModal({ open: false, data: null });
      const data = await api.getAdminPromoCodes();
      setPromoCodes(data);
    } catch (error) {
      alert(error.response?.data?.detail || 'Ошибка при сохранении промокода');
    }
  };

  const handleSaveDelivery = async (option) => {
    try {
      await api.updateDeliveryOption(option.id, { ...option, price: parseInt(option.price, 10) || 0 });
      setDeliveryOptions(await api.getAdminDeliveryOptions());
    } catch (error) {
      alert(error.response?.data?.detail || 'Ошибка при сохранении способа доставки');
    }
  };

  const handleCreateDelivery = async (event) => {
    event.preventDefault();
    try {
      await api.createDeliveryOption({ ...newDelivery, price: parseInt(newDelivery.price, 10) || 0 });
      setDeliveryOptions(await api.getAdminDeliveryOptions());
      setNewDelivery({ id: '', name: '', price: 0, description: '', requires_address: false, requires_city: false });
    } catch (error) {
      alert(error.response?.data?.detail || 'Ошибка при добавлении способа доставки');
    }
  };

  const handleVerifyPickup = async (event) => {
    event.preventDefault();
    setPickupResult(null);
    setPickupError('');
    try {
      setPickupResult(await api.verifyPickupCode(pickupCode));
    } catch (error) {
      setPickupError(error.response?.data?.detail || 'Не удалось проверить код');
    }
  };

  const handleIssuePickup = async () => {
    if (!pickupResult || pickupResult.pickup_issued) return;
    if (!window.confirm(`Выдать заказ #${pickupResult.id} клиенту ${pickupResult.customer.name}?`)) return;
    try {
      await api.issuePickupOrder(pickupCode);
      setPickupResult(prev => ({ ...prev, pickup_issued: true, status: 'delivered', message: 'Заказ выдан' }));
      await loadOrders();
    } catch (error) {
      setPickupError(error.response?.data?.detail || 'Не удалось отметить выдачу');
    }
  };

  const handleUpdateCoins = async (email, amount, action) => {
    try {
      await api.updateUserCoins(email, parseInt(amount), action);
      setCoinsModal({ open: false });
      const data = await api.getAdminUsers();
      setUsers(data);
      alert('Баланс обновлен');
    } catch (error) {
      alert(error.response?.data?.detail || 'Ошибка при обновлении баланса');
    }
  };

  const handleSaveBanner = async (bannerData) => {
    try {
      if (bannerModal.data?.id) {
        await api.updateBanner(bannerModal.data.id, bannerData);
      } else {
        await api.createBanner(bannerData);
      }
      setBannerModal({ open: false, data: null });
      const data = await api.getAdminBanners();
      setBanners(data);
    } catch (error) {
      alert('Ошибка при сохранении баннера');
    }
  };

  const handleDeleteBanner = async (bannerId) => {
    if (!window.confirm('Удалить баннер?')) return;
    try {
      await api.deleteBanner(bannerId);
      setBanners(prev => prev.filter(b => b.id !== bannerId));
    } catch (error) {
      alert('Ошибка при удалении баннера');
    }
  };

  const exportOrders = () => {
    const csvContent = orders.map(order =>
      `${order.id},${order.created_at},${orderStatuses[order.status] || order.status},${order.customer?.name},${order.customer?.phone},${order.total}`
    ).join('\n');
    const header = 'Номер,Дата,Статус,Клиент,Телефон,Сумма\n';
    const blob = new Blob(['\ufeff' + header + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'orders.csv';
    link.click();
  };

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('/api')) return `${BACKEND_URL}${url}`;
    return url;
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-brand-surface dark:bg-[#1E1E1E] flex items-center justify-center transition-colors">
        <div className="bg-brand-surface dark:bg-brand-surface p-8 rounded-2xl shadow-lg w-full max-w-md">
          <img src="/brand/logo.png" alt="tistore" className="w-48 mx-auto mb-6" /><h1 className="text-2xl font-medium mb-6 text-center text-brand-text dark:text-brand-text">Админ-панель</h1>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-brand-text dark:text-brand-text">Логин</Label>
              <Input
                id="username"
                value={loginData.username}
                onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                placeholder="Введите логин"
                className="rounded-xl dark:bg-brand-surface dark:border-brand-border dark:text-brand-text"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-brand-text dark:text-brand-text">Пароль</Label>
              <Input
                id="password"
                type="password"
                value={loginData.password}
                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                placeholder="Введите пароль"
                className="rounded-xl dark:bg-brand-surface dark:border-brand-border dark:text-brand-text"
              />
            </div>
            {loginError && <p className="text-brand-text text-sm">{loginError}</p>}
            <Button type="submit" className="w-full bg-brand-accent dark:bg-brand-accent text-brand-text dark:text-brand-text hover:bg-brand-surface dark:hover:bg-brand-surface rounded-xl" disabled={isLoading}>
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Войти'}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-surface dark:bg-[#1E1E1E] transition-colors">
      {/* Admin Header */}
      <header className="bg-brand-surface border-b border-brand-border text-brand-text">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div><img src="/brand/logo.png" alt="tistore" className="w-32 mb-2" /><h1 className="text-sm">Админ-панель</h1></div>
          <Button
            variant="outline"
            onClick={handleLogout}
            className="text-brand-text border-white hover:bg-brand-surface hover:text-brand-text rounded-xl"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Выйти
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-brand-surface dark:bg-brand-surface p-6 rounded-2xl shadow">
              <div className="flex items-center gap-3">
                <BarChart3 className="w-8 h-8 text-brand-text" />
                <div>
                  <p className="text-brand-text dark:text-brand-text text-sm">Общая выручка</p>
                  <p className="text-2xl font-bold text-brand-text dark:text-brand-text">{stats.total_revenue?.toLocaleString('ru-RU')} ₽</p>
                </div>
              </div>
            </div>
            <div className="bg-brand-surface dark:bg-brand-surface p-6 rounded-2xl shadow">
              <div className="flex items-center gap-3">
                <ShoppingCart className="w-8 h-8 text-brand-text" />
                <div>
                  <p className="text-brand-text dark:text-brand-text text-sm">Всего заказов</p>
                  <p className="text-2xl font-bold text-brand-text dark:text-brand-text">{stats.total_orders}</p>
                </div>
              </div>
            </div>
            <div className="bg-brand-surface dark:bg-brand-surface p-6 rounded-2xl shadow">
              <div className="flex items-center gap-3">
                <Package className="w-8 h-8 text-brand-accent" />
                <div>
                  <p className="text-brand-text dark:text-brand-text text-sm">Новых заказов</p>
                  <p className="text-2xl font-bold text-brand-accent">{stats.new_orders}</p>
                </div>
              </div>
            </div>
            <div className="bg-brand-surface dark:bg-brand-surface p-6 rounded-2xl shadow">
              <div className="flex items-center gap-3">
                <Tags className="w-8 h-8 text-brand-text" />
                <div>
                  <p className="text-brand-text dark:text-brand-text text-sm">Товаров</p>
                  <p className="text-2xl font-bold text-brand-text dark:text-brand-text">{stats.total_products}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        <Tabs defaultValue="orders" className="w-full">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 h-auto gap-1 mb-6 dark:bg-brand-surface">
            <TabsTrigger value="inventory">Склад / касса</TabsTrigger>
            <TabsTrigger value="support">Поддержка</TabsTrigger>
            <TabsTrigger value="orders" className="flex items-center gap-2 data-[state=active]:bg-brand-accent dark:data-[state=active]:bg-brand-accent">
              <ShoppingCart className="w-4 h-4" />
              Заказы
            </TabsTrigger>
            <TabsTrigger value="products" className="flex items-center gap-2 data-[state=active]:bg-brand-accent dark:data-[state=active]:bg-brand-accent">
              <Package className="w-4 h-4" />
              Товары
            </TabsTrigger>
            <TabsTrigger value="banners" className="flex items-center gap-2 data-[state=active]:bg-brand-accent dark:data-[state=active]:bg-brand-accent">
              <Image className="w-4 h-4" />
              Баннеры
            </TabsTrigger>
            <TabsTrigger value="promos" className="flex items-center gap-2 data-[state=active]:bg-brand-accent dark:data-[state=active]:bg-brand-accent">
              <Tags className="w-4 h-4" />
              Промокоды
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-2 data-[state=active]:bg-brand-accent dark:data-[state=active]:bg-brand-accent">
              <Users className="w-4 h-4" />
              Пользователи
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2 data-[state=active]:bg-brand-accent dark:data-[state=active]:bg-brand-accent">
              <Settings className="w-4 h-4" />
              Настройки
            </TabsTrigger>
          </TabsList>

          {/* Orders Tab */}
          <TabsContent value="inventory"><InventoryAdmin products={products} /></TabsContent>
          <TabsContent value="support"><AdminSupport /></TabsContent>
          <TabsContent value="orders">
            <div className="bg-brand-surface dark:bg-brand-surface rounded-2xl shadow p-6">
              <div className="flex flex-wrap gap-3 justify-between items-center mb-6">
                <h2 className="text-xl font-medium text-brand-text dark:text-brand-text">Управление заказами</h2>
                <div className="flex gap-4">
                  <Select value={selectedOrderStatus} onValueChange={setSelectedOrderStatus}>
                    <SelectTrigger className="w-48 rounded-xl dark:bg-brand-surface dark:border-brand-border">
                      <SelectValue placeholder="Фильтр по статусу" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Все заказы</SelectItem>
                      {Object.entries(orderStatuses).map(([key, value]) => (
                        <SelectItem key={key} value={key}>{value}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" onClick={exportOrders} className="rounded-xl">
                    <Download className="w-4 h-4 mr-2" />
                    Экспорт
                  </Button>
                </div>
              </div>

              <div className="rounded-xl border dark:border-brand-border p-4 mb-6">
                <h3 className="font-medium text-brand-text dark:text-brand-text mb-3">Выдача заказа самовывозом</h3>
                <form onSubmit={handleVerifyPickup} className="flex flex-wrap gap-2">
                  <Input value={pickupCode} onChange={e => setPickupCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="Код из 6 цифр" inputMode="numeric" maxLength={6} className="max-w-xs dark:bg-brand-surface dark:border-brand-border dark:text-brand-text" />
                  <Button type="submit" variant="outline" disabled={pickupCode.length !== 6}>Проверить код</Button>
                </form>
                {pickupError && <p className="text-sm text-brand-text mt-2">{pickupError}</p>}
                {pickupResult && (
                  <div className="mt-4 rounded-lg bg-brand-surface dark:bg-brand-surface/50 p-4">
                    <p className="font-medium text-brand-text dark:text-brand-text">{pickupResult.message}: заказ #{pickupResult.id}</p>
                    <p className="text-sm text-brand-text dark:text-brand-text">{pickupResult.customer.name} · {pickupResult.customer.phone}</p>
                    <p className="text-sm text-brand-text dark:text-brand-text mt-1">{pickupResult.items.map(item => `${item.name} (${item.size}) × ${item.quantity}`).join(', ')}</p>
                    <p className="text-sm text-brand-text dark:text-brand-text mt-1">К оплате: {pickupResult.total?.toLocaleString('ru-RU')} ₽</p>
                    {pickupResult.pickup_issued ? (
                      <p className="text-sm text-brand-text dark:text-brand-text mt-3">Заказ уже выдан</p>
                    ) : (
                      <Button type="button" onClick={handleIssuePickup} className="mt-3 bg-brand-accent text-brand-text">Подтвердить выдачу</Button>
                    )}
                  </div>
                )}
              </div>

              {isLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-brand-text dark:text-brand-text" />
                </div>
              ) : orders.length > 0 ? (
                <div className="space-y-4">
                  {orders.map(order => (
                    <div key={order.id} className="border dark:border-brand-border rounded-xl p-4">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <p className="font-medium text-lg text-brand-text dark:text-brand-text">Заказ #{order.id}</p>
                          <p className="text-sm text-brand-text dark:text-brand-text">
                            {new Date(order.created_at).toLocaleString('ru-RU')}
                          </p>
                          <p className="text-sm mt-2 text-brand-text dark:text-brand-text">
                            <strong>Клиент:</strong> {order.customer?.name}
                          </p>
                          <p className="text-sm text-brand-text dark:text-brand-text"><strong>Тел:</strong> {order.customer?.phone}</p>
                          {order.customer?.email && (
                            <p className="text-sm text-brand-text dark:text-brand-text"><strong>Email:</strong> {order.customer.email}</p>
                          )}
                          {order.pickup_code && (
                            <p className="text-sm mt-1 text-brand-text dark:text-brand-text"><strong>Код самовывоза:</strong> <span className="font-mono font-bold tracking-widest">{order.pickup_code}</span>{order.pickup_issued ? ' · выдан' : ' · ожидает выдачи'}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <Select
                            value={order.status}
                            onValueChange={(value) => handleUpdateOrderStatus(order.id, value)}
                          >
                            <SelectTrigger className="w-40 rounded-xl dark:bg-brand-surface dark:border-brand-border">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(orderStatuses).map(([key, value]) => (
                                <SelectItem key={key} value={key}>{value}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xl font-bold mt-4 text-brand-text dark:text-brand-text">{order.total?.toLocaleString('ru-RU')} ₽</p>
                        </div>
                      </div>
                      <div className="border-t dark:border-brand-border pt-4">
                        <p className="text-sm font-medium mb-2 text-brand-text dark:text-brand-text">Товары:</p>
                        {order.items?.map((item, idx) => (
                          <p key={idx} className="text-sm text-brand-text dark:text-brand-text">
                            {item.name} ({item.size}) x {item.quantity} = {(item.price * item.quantity).toLocaleString('ru-RU')} ₽
                          </p>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-brand-text dark:text-brand-text py-8">Заказов нет</p>
              )}
            </div>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products">
            <div className="bg-brand-surface dark:bg-brand-surface rounded-2xl shadow p-6">
              <div className="flex flex-wrap gap-3 justify-between items-center mb-6">
                <h2 className="text-xl font-medium text-brand-text dark:text-brand-text">Управление товарами</h2>
                <Button 
                  className="bg-brand-accent dark:bg-brand-accent text-brand-text dark:text-brand-text hover:bg-brand-surface dark:hover:bg-brand-surface rounded-xl"
                  onClick={() => setProductModal({ open: true, data: null })}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Добавить товар
                </Button>
              </div>

              {products.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b dark:border-brand-border">
                        <th className="text-left py-3 px-4 text-brand-text dark:text-brand-text">Фото</th>
                        <th className="text-left py-3 px-4 text-brand-text dark:text-brand-text">Артикул</th>
                        <th className="text-left py-3 px-4 text-brand-text dark:text-brand-text">Название</th>
                        <th className="text-left py-3 px-4 text-brand-text dark:text-brand-text">Цена</th>
                        <th className="text-left py-3 px-4 text-brand-text dark:text-brand-text">Размеры</th>
                        <th className="text-left py-3 px-4 text-brand-text dark:text-brand-text">Действия</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.map(product => (
                        <tr key={product.id} className="border-b dark:border-brand-border hover:bg-brand-surface dark:hover:bg-brand-surface">
                          <td className="py-3 px-4">
                            {product.images?.[0] ? (
                              <img
                                src={getImageUrl(product.images[0])}
                                alt={product.name}
                                className="w-16 h-20 object-cover rounded-lg"
                              />
                            ) : (
                              <div className="w-16 h-20 bg-brand-surface dark:bg-brand-surface rounded-lg flex items-center justify-center text-brand-text dark:text-brand-text text-xs">
                                Нет фото
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-4 text-brand-text dark:text-brand-text">{product.article}</td>
                          <td className="py-3 px-4 text-brand-text dark:text-brand-text">{product.name}{product.active === false && <span className="block text-xs opacity-60 mt-1">Скрыт с сайта</span>}</td>
                          <td className="py-3 px-4">
                            {product.old_price && (
                              <span className="line-through text-brand-text mr-2">
                                {product.old_price.toLocaleString('ru-RU')} ₽
                              </span>
                            )}
                            <span className="font-medium text-brand-text dark:text-brand-text">{product.price?.toLocaleString('ru-RU')} ₽</span>
                          </td>
                          <td className="py-3 px-4 text-brand-text dark:text-brand-text">{product.sizes?.join(', ')}</td>
                          <td className="py-3 px-4">
                            <div className="flex gap-2">
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="rounded-lg dark:border-brand-border"
                                onClick={() => setProductModal({ open: true, data: product })}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button type="button" variant="outline" size="sm" disabled={visibilityBusy !== null} onClick={() => handleToggleProduct(product)} className="rounded-lg dark:border-brand-border">
                                {visibilityBusy === product.id ? 'Сохранение…' : product.active === false ? 'Показать' : 'Скрыть'}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteProduct(product.id)}
                                className="text-brand-text hover:text-brand-text rounded-lg dark:border-brand-border"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center text-brand-text dark:text-brand-text py-8">Товаров нет</p>
              )}
            </div>
          </TabsContent>

          {/* Banners Tab */}
          <TabsContent value="banners">
            <div className="bg-brand-surface dark:bg-brand-surface rounded-2xl shadow p-6">
              <div className="flex flex-wrap gap-3 justify-between items-center mb-6">
                <h2 className="text-xl font-medium text-brand-text dark:text-brand-text">Управление баннерами</h2>
                <Button 
                  className="bg-brand-accent dark:bg-brand-accent text-brand-text dark:text-brand-text hover:bg-brand-surface dark:hover:bg-brand-surface rounded-xl"
                  onClick={() => setBannerModal({ open: true, data: null })}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Добавить баннер
                </Button>
              </div>

              {banners.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {banners.map(banner => (
                    <div key={banner.id} className="border dark:border-brand-border rounded-xl overflow-hidden">
                      <div className="aspect-[1920/560] bg-brand-surface dark:bg-brand-surface">
                        {banner.image && (
                          <img
                            src={getImageUrl(banner.image)}
                            alt={banner.title || 'Баннер'}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      <div className="p-4">
                        <p className="font-medium text-brand-text dark:text-brand-text">Баннер · 24:7</p>
                        
                        <p className="text-xs text-brand-text dark:text-brand-text mt-2">Порядок: {banner.order}</p>
                        <div className="flex gap-2 mt-3">
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="rounded-lg flex-1 dark:border-brand-border"
                            onClick={() => setBannerModal({ open: true, data: banner })}
                          >
                            <Edit2 className="w-4 h-4 mr-1" />
                            Изменить
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteBanner(banner.id)}
                            className="text-brand-text hover:text-brand-text rounded-lg dark:border-brand-border"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-brand-text dark:text-brand-text py-8">Баннеров нет. Блок баннеров на сайте скрыт.</p>
              )}
            </div>
          </TabsContent>

          {/* Promo Codes Tab */}
          <TabsContent value="promos">
            <div className="bg-brand-surface dark:bg-brand-surface rounded-2xl shadow p-6">
              <div className="flex flex-wrap gap-3 justify-between items-center mb-6">
                <h2 className="text-xl font-medium text-brand-text dark:text-brand-text">Управление промокодами</h2>
                <Button 
                  className="bg-brand-accent dark:bg-brand-accent text-brand-text dark:text-brand-text hover:bg-brand-surface dark:hover:bg-brand-surface rounded-xl"
                  onClick={() => setPromoModal({ open: true, data: null })}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Добавить промокод
                </Button>
              </div>

              {promoCodes.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b dark:border-brand-border">
                        <th className="text-left py-3 px-4 text-brand-text dark:text-brand-text">Код</th>
                        <th className="text-left py-3 px-4 text-brand-text dark:text-brand-text">Тип</th>
                        <th className="text-left py-3 px-4 text-brand-text dark:text-brand-text">Значение</th>
                        <th className="text-left py-3 px-4 text-brand-text dark:text-brand-text">От суммы заказа</th>
                        <th className="text-left py-3 px-4 text-brand-text dark:text-brand-text">Использовано</th>
                        <th className="text-left py-3 px-4 text-brand-text dark:text-brand-text">Действует до</th>
                        <th className="text-left py-3 px-4 text-brand-text dark:text-brand-text">Статус</th>
                        <th className="text-left py-3 px-4 text-brand-text dark:text-brand-text">Действия</th>
                      </tr>
                    </thead>
                    <tbody>
                      {promoCodes.map((promo) => (
                        <tr key={promo.id} className="border-b dark:border-brand-border hover:bg-brand-surface dark:hover:bg-brand-surface">
                          <td className="py-3 px-4 font-mono font-medium text-brand-text dark:text-brand-text">{promo.code}</td>
                          <td className="py-3 px-4 text-brand-text dark:text-brand-text">{promo.type === 'percent' ? 'Процент' : 'Фикс.'}</td>
                          <td className="py-3 px-4 text-brand-text dark:text-brand-text">
                            {promo.type === 'percent' ? `${promo.value}%` : `${promo.value} ₽`}
                          </td>
                          <td className="py-3 px-4 text-brand-text dark:text-brand-text">{(promo.min_order_amount || 0).toLocaleString('ru-RU')} ₽</td>
                          <td className="py-3 px-4 text-brand-text dark:text-brand-text">{promo.used_count || 0} / {promo.limit}</td>
                          <td className="py-3 px-4 text-brand-text dark:text-brand-text">{promo.expires_at}</td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              promo.active ? 'bg-brand-surface dark:bg-brand-accent/30 text-brand-text dark:text-brand-text' : 'bg-brand-surface dark:bg-brand-surface text-brand-text dark:text-brand-text'
                            }`}>
                              {promo.active ? 'Активен' : 'Неактивен'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeletePromo(promo.id)}
                              className="text-brand-text hover:text-brand-text rounded-lg dark:border-brand-border"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center text-brand-text dark:text-brand-text py-8">Промокодов нет</p>
              )}
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="bg-brand-surface dark:bg-brand-surface rounded-2xl shadow p-6">
              <div className="flex flex-wrap gap-3 justify-between items-center mb-6">
                <h2 className="text-xl font-medium text-brand-text dark:text-brand-text">Пользователи</h2>
                <Button 
                  variant="outline"
                  onClick={() => setCoinsModal({ open: true })}
                  className="rounded-xl dark:border-brand-border"
                >
                  Управление coins
                </Button>
              </div>

              {users.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b dark:border-brand-border">
                        <th className="text-left py-3 px-4 text-brand-text dark:text-brand-text">Email</th>
                        <th className="text-left py-3 px-4 text-brand-text dark:text-brand-text">Имя</th>
                        <th className="text-left py-3 px-4 text-brand-text dark:text-brand-text">Телефон</th>
                        <th className="text-left py-3 px-4 text-brand-text dark:text-brand-text">Coins</th>
                        <th className="text-left py-3 px-4 text-brand-text dark:text-brand-text">Дата регистрации</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr key={user.id} className="border-b dark:border-brand-border hover:bg-brand-surface dark:hover:bg-brand-surface">
                          <td className="py-3 px-4 text-brand-text dark:text-brand-text">{user.email}</td>
                          <td className="py-3 px-4 text-brand-text dark:text-brand-text">{user.name || '—'}</td>
                          <td className="py-3 px-4 text-brand-text dark:text-brand-text">{user.phone || '—'}</td>
                          <td className="py-3 px-4 font-medium text-brand-text dark:text-brand-text">{user.coins || 0}</td>
                          <td className="py-3 px-4 text-brand-text dark:text-brand-text">
                            {user.created_at ? new Date(user.created_at).toLocaleDateString('ru-RU') : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center text-brand-text dark:text-brand-text py-8">Пользователей нет</p>
              )}
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="bg-brand-surface dark:bg-brand-surface rounded-2xl shadow p-6">
              <h2 className="text-xl font-medium mb-6 text-brand-text dark:text-brand-text">Настройки</h2>
              <div className="space-y-6">
                <section className="border-b dark:border-brand-border pb-6">
                  <h3 className="font-medium mb-1 text-brand-text dark:text-brand-text">Способы получения и доставки</h3>
                  <p className="text-sm text-brand-text dark:text-brand-text mb-4">Меняйте стоимость, описание и обязательные поля оформления заказа.</p>
                  <div className="space-y-4">
                    {deliveryOptions.map(option => (
                      <div key={option.id} className="grid grid-cols-1 md:grid-cols-2 gap-3 rounded-xl border dark:border-brand-border p-4">
                        <Input aria-label="Название способа" value={option.name} onChange={e => setDeliveryOptions(prev => prev.map(item => item.id === option.id ? { ...item, name: e.target.value } : item))} className="dark:bg-brand-surface dark:border-brand-border dark:text-brand-text" />
                        <Input aria-label="Стоимость доставки" type="number" min="0" value={option.price} onChange={e => setDeliveryOptions(prev => prev.map(item => item.id === option.id ? { ...item, price: e.target.value } : item))} placeholder="Стоимость, ₽" className="dark:bg-brand-surface dark:border-brand-border dark:text-brand-text" />
                        <Input aria-label="Описание способа" value={option.description || ''} onChange={e => setDeliveryOptions(prev => prev.map(item => item.id === option.id ? { ...item, description: e.target.value } : item))} placeholder="Описание / адрес самовывоза" className="dark:bg-brand-surface dark:border-brand-border dark:text-brand-text md:col-span-2" />
                        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-brand-text dark:text-brand-text md:col-span-2">
                          <label className="flex items-center gap-2"><input type="checkbox" checked={option.requires_address} onChange={e => setDeliveryOptions(prev => prev.map(item => item.id === option.id ? { ...item, requires_address: e.target.checked, requires_city: e.target.checked ? false : item.requires_city } : item))} />Запрашивать адрес</label>
                          <label className="flex items-center gap-2"><input type="checkbox" checked={option.requires_city} onChange={e => setDeliveryOptions(prev => prev.map(item => item.id === option.id ? { ...item, requires_city: e.target.checked, requires_address: e.target.checked ? false : item.requires_address } : item))} />Запрашивать город</label>
                          <label className="flex items-center gap-2"><input type="checkbox" checked={option.active} onChange={e => setDeliveryOptions(prev => prev.map(item => item.id === option.id ? { ...item, active: e.target.checked } : item))} />Показывать при оформлении</label>
                          <Button type="button" size="sm" variant="outline" onClick={() => handleSaveDelivery(option)} className="ml-auto">Сохранить</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <form onSubmit={handleCreateDelivery} className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-5 rounded-xl bg-brand-surface dark:bg-brand-surface/40 p-4">
                    <h4 className="font-medium text-brand-text dark:text-brand-text md:col-span-2">Добавить способ доставки</h4>
                    <Input value={newDelivery.id} onChange={e => setNewDelivery(prev => ({ ...prev, id: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') }))} placeholder="Код, например courier" required className="dark:bg-brand-surface dark:border-brand-border dark:text-brand-text" />
                    <Input value={newDelivery.name} onChange={e => setNewDelivery(prev => ({ ...prev, name: e.target.value }))} placeholder="Название" required className="dark:bg-brand-surface dark:border-brand-border dark:text-brand-text" />
                    <Input type="number" min="0" value={newDelivery.price} onChange={e => setNewDelivery(prev => ({ ...prev, price: e.target.value }))} placeholder="Стоимость, ₽" className="dark:bg-brand-surface dark:border-brand-border dark:text-brand-text" />
                    <Input value={newDelivery.description} onChange={e => setNewDelivery(prev => ({ ...prev, description: e.target.value }))} placeholder="Описание" className="dark:bg-brand-surface dark:border-brand-border dark:text-brand-text" />
                    <label className="flex items-center gap-2 text-sm text-brand-text dark:text-brand-text"><input type="checkbox" checked={newDelivery.requires_address} onChange={e => setNewDelivery(prev => ({ ...prev, requires_address: e.target.checked, requires_city: false }))} />Нужен адрес</label>
                    <label className="flex items-center gap-2 text-sm text-brand-text dark:text-brand-text"><input type="checkbox" checked={newDelivery.requires_city} onChange={e => setNewDelivery(prev => ({ ...prev, requires_city: e.target.checked, requires_address: false }))} />Нужен город</label>
                    <Button type="submit" className="md:col-span-2 bg-brand-accent text-brand-text">Добавить способ получения</Button>
                  </form>
                </section>
                <div>
                  <h3 className="font-medium mb-3 text-brand-text dark:text-brand-text">Экспорт данных</h3>
                  <div className="flex gap-4">
                    <Button variant="outline" onClick={exportOrders} className="rounded-xl dark:border-brand-border">
                      <Download className="w-4 h-4 mr-2" />
                      Экспорт заказов
                    </Button>
                  </div>
                </div>
                <div className="border-t dark:border-brand-border pt-6">
                  <h3 className="font-medium mb-3 text-brand-text dark:text-brand-text">Telegram уведомления</h3>
                  <p className="text-sm text-brand-text dark:text-brand-text">
                    Уведомления о новых заказах отправляются в Telegram бот.
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Product Modal */}
      <ProductModal 
        open={productModal.open}
        data={productModal.data}
        onClose={() => setProductModal({ open: false, data: null })}
        onSave={handleSaveProduct}
      />

      {/* Promo Modal */}
      <PromoModal
        open={promoModal.open}
        onClose={() => setPromoModal({ open: false, data: null })}
        onSave={handleSavePromo}
      />

      {/* Coins Modal */}
      <CoinsModal
        open={coinsModal.open}
        onClose={() => setCoinsModal({ open: false })}
        onSave={handleUpdateCoins}
      />

      {/* Banner Modal */}
      <BannerModal
        open={bannerModal.open}
        data={bannerModal.data}
        onClose={() => setBannerModal({ open: false, data: null })}
        onSave={handleSaveBanner}
      />
    </div>
  );
};

// Product Modal Component with File Upload
const ProductModal = ({ open, data, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    old_price: '',
    sizes: '',
    category: '',
    collection: '',
    description: '',
    images: []
  });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (data) {
      setFormData({
        name: data.name || '',
        price: data.price || '',
        old_price: data.old_price || '',
        sizes: data.sizes?.join(', ') || '',
        category: data.category || '',
        collection: data.collection || '',
        description: data.description || '',
        images: data.images || []
      });
    } else {
      setFormData({
        name: '',
        price: '',
        old_price: '',
        sizes: 'XS, S, M, L, XL',
        category: '',
        collection: '',
        description: '',
        images: []
      });
    }
  }, [data]);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const result = await api.uploadMultipleFiles(files);
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...result.urls]
      }));
    } catch (error) {
      alert('Ошибка при загрузке файлов');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const productData = {
      name: formData.name,
      price: parseInt(formData.price),
      old_price: formData.old_price ? parseInt(formData.old_price) : null,
      sizes: formData.sizes.split(',').map(s => s.trim()).filter(Boolean),
      category: formData.category,
      collection: formData.collection,
      description: formData.description,
      images: formData.images
    };
    onSave(productData);
  };

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('/api')) return `${BACKEND_URL}${url}`;
    return url;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl dark:bg-brand-surface">
        <DialogHeader>
          <DialogTitle className="dark:text-brand-text">{data ? 'Редактировать товар' : 'Добавить товар'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="dark:text-brand-text">Название *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="rounded-xl dark:bg-brand-surface dark:border-brand-border dark:text-brand-text"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="dark:text-brand-text">Категория</Label>
              <Input
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder="Футболки, Джинсы, и т.д."
                className="rounded-xl dark:bg-brand-surface dark:border-brand-border dark:text-brand-text"
              />
            </div>
            <div className="space-y-2">
              <Label className="dark:text-brand-text">Цена *</Label>
              <Input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                className="rounded-xl dark:bg-brand-surface dark:border-brand-border dark:text-brand-text"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="dark:text-brand-text">Старая цена (для скидки)</Label>
              <Input
                type="number"
                value={formData.old_price}
                onChange={(e) => setFormData({ ...formData, old_price: e.target.value })}
                className="rounded-xl dark:bg-brand-surface dark:border-brand-border dark:text-brand-text"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="dark:text-brand-text">Размеры (через запятую)</Label>
              <Input
                value={formData.sizes}
                onChange={(e) => setFormData({ ...formData, sizes: e.target.value })}
                placeholder="XS, S, M, L, XL"
                className="rounded-xl dark:bg-brand-surface dark:border-brand-border dark:text-brand-text"
              />
            </div>
            <div className="space-y-2">
              <Label className="dark:text-brand-text">Коллекция</Label>
              <Input
                value={formData.collection}
                onChange={(e) => setFormData({ ...formData, collection: e.target.value })}
                placeholder="Весна 2026, Базовая и т.д."
                className="rounded-xl dark:bg-brand-surface dark:border-brand-border dark:text-brand-text"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="dark:text-brand-text">Описание</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="rounded-xl dark:bg-brand-surface dark:border-brand-border dark:text-brand-text"
            />
          </div>
          <div className="space-y-2">
            <Label className="dark:text-brand-text">Фотографии</Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {formData.images.map((img, idx) => (
                <div key={idx} className="relative w-20 h-24 rounded-lg overflow-hidden">
                  <img src={getImageUrl(img)} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeImage(idx)}
                    className="absolute top-1 right-1 w-5 h-5 bg-brand-accent text-brand-text rounded-full flex items-center justify-center"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              multiple
              accept="image/*"
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="rounded-xl dark:border-brand-border"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              Загрузить фото
            </Button>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="rounded-xl dark:border-brand-border">Отмена</Button>
            <Button type="submit" className="bg-brand-accent dark:bg-brand-accent text-brand-text dark:text-brand-text hover:bg-brand-surface dark:hover:bg-brand-surface rounded-xl">Сохранить</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Banner images are cropped locally before using the shared upload endpoint.
const BannerModal = ({ open, data, onClose, onSave }) => {
  const [formData, setFormData] = useState({ image: '', order: 0 });
  const [source, setSource] = useState(null);
  const [position, setPosition] = useState({ x: 50, y: 50 });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const version = useRef(0);
  useEffect(() => {
    version.current += 1;
    setFormData({ image: data?.image || '', order: data?.order || 0 });
    setSource(null); setError(''); setBusy(false);
  }, [open, data]);
  useEffect(() => () => { if (source) URL.revokeObjectURL(source.url); }, [source]);
  const chooseImage = async (event) => {
    const file = event.target.files[0]; event.target.value = '';
    if (!file) return;
    setError('');
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setError('Выберите JPG, PNG или WebP.'); return;
    }
    if (file.size > 20 * 1024 * 1024) { setError('Максимальный размер файла — 20 МБ.'); return; }
    const request = ++version.current;
    const url = URL.createObjectURL(file);
    const img = new window.Image();
    img.src = url;
    try {
      await img.decode();
      if (request !== version.current) { URL.revokeObjectURL(url); return; }
      setSource({ url, img, file }); setPosition({ x: 50, y: 50 });
    } catch { URL.revokeObjectURL(url); setError('Не удалось прочитать изображение.'); }
  };
  const applyCrop = async () => {
    setBusy(true); setError('');
    const request = version.current;
    try {
      const { img, file } = source;
      const scale = Math.max(1920 / img.naturalWidth, 560 / img.naturalHeight);
      const width = 1920 / scale, height = 560 / scale;
      // Keep native detail; never enlarge a small original just to meet a pixel count.
      const outputWidth = Math.min(3840, Math.floor(width));
      const outputHeight = Math.max(1, Math.round(outputWidth * 560 / 1920));
      const canvas = document.createElement('canvas'); canvas.width = outputWidth; canvas.height = outputHeight;
      canvas.getContext('2d').imageSmoothingQuality = 'high';
      canvas.getContext('2d').drawImage(img,
        (img.naturalWidth - width) * position.x / 100,
        (img.naturalHeight - height) * position.y / 100,
        width, height, 0, 0, outputWidth, outputHeight);
      const unchanged = img.naturalWidth === outputWidth && img.naturalHeight === outputHeight;
      const blob = unchanged ? file : await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      if (!blob) throw new Error('Encoding failed');
      const result = await api.uploadFile(unchanged ? file : new File([blob], `banner-${outputWidth}x${outputHeight}.png`, { type: 'image/png' }));
      if (request === version.current) { setFormData(prev => ({ ...prev, image: result.url })); setSource(null); }
    } catch { if (request === version.current) setError('Не удалось загрузить баннер. Попробуйте ещё раз.'); }
    finally { if (request === version.current) setBusy(false); }
  };
  const submit = async event => {
    event.preventDefault(); if (busy || source || !formData.image) return;
    setBusy(true);
    try { await onSave({ ...formData, title: '', subtitle: '' }); }
    finally { setBusy(false); }
  };
  const imageUrl = formData.image.startsWith('/api') ? `${BACKEND_URL}${formData.image}` : formData.image;
  return (
    <Dialog open={open} onOpenChange={() => { if (!busy) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[90dvh] overflow-y-auto rounded-2xl bg-brand-surface text-brand-text">
        <DialogHeader><DialogTitle>{data ? 'Редактировать баннер' : 'Добавить баннер'}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <p className="text-sm">Для чёткого баннера используйте оригинал 3840 × 1120 px (пропорции 24:7). JPG, PNG или WebP, до 20 МБ. Обрезка сохраняется в PNG без потерь, до 3840 × 1120 px. Маленькие изображения не растягиваются.</p>
          {(source || formData.image) && <div className="aspect-[1920/560] overflow-hidden rounded-xl bg-black">
            <img src={source?.url || imageUrl} alt="Предпросмотр баннера" className="w-full h-full object-cover" style={{ objectPosition: source ? `${position.x}% ${position.y}%` : 'center' }} />
          </div>}
          <input type="file" ref={fileInputRef} accept="image/jpeg,image/png,image/webp" onChange={chooseImage} className="hidden" />
          <Button type="button" variant="outline" disabled={busy} onClick={() => fileInputRef.current?.click()} className="w-full">Выбрать изображение</Button>
          {source && <div className="space-y-3">
            {(source.img.naturalWidth < 3840 || source.img.naturalHeight < 1120) && <p className="text-sm">Для чёткости на Retina нужен оригинал не менее 3840 × 1120 px. Этот файл будет сохранён с доступным разрешением.</p>}
            <label className="block">По горизонтали<input aria-label="Положение кадра по горизонтали" className="block w-full accent-[#AD2831]" type="range" min="0" max="100" value={position.x} disabled={busy} onChange={e => setPosition(prev => ({ ...prev, x: Number(e.target.value) }))} /></label>
            <label className="block">По вертикали<input aria-label="Положение кадра по вертикали" className="block w-full accent-[#AD2831]" type="range" min="0" max="100" value={position.y} disabled={busy} onChange={e => setPosition(prev => ({ ...prev, y: Number(e.target.value) }))} /></label>
            <Button type="button" disabled={busy} onClick={applyCrop} className="w-full bg-brand-accent">{busy ? 'Загрузка…' : 'Применить кадр и загрузить'}</Button>
          </div>}
          {error && <p role="alert">{error}</p>}
          <label className="block">Порядок отображения<Input type="number" value={formData.order} disabled={busy} onChange={e => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))} /></label>
          <DialogFooter>
            <Button type="button" variant="outline" disabled={busy} onClick={onClose}>Отмена</Button>
            <Button type="submit" disabled={busy || !!source || !formData.image} className="bg-brand-accent">{busy ? 'Сохранение…' : 'Сохранить'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Promo Modal Component
const PromoModal = ({ open, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    code: '',
    type: 'percent',
    value: '',
    min_order_amount: '0',
    limit: '100',
    expires_at: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      code: formData.code,
      type: formData.type,
      value: parseInt(formData.value),
      min_order_amount: parseInt(formData.min_order_amount, 10) || 0,
      limit: parseInt(formData.limit),
      expires_at: formData.expires_at
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-2xl dark:bg-brand-surface">
        <DialogHeader>
          <DialogTitle className="dark:text-brand-text">Добавить промокод</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label className="dark:text-brand-text">Код *</Label>
            <Input
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
              placeholder="SALE10"
              className="rounded-xl dark:bg-brand-surface dark:border-brand-border dark:text-brand-text"
              required
            />
          </div>
          <div className="space-y-2">
            <Label className="dark:text-brand-text">Минимальная сумма заказа, ₽</Label>
            <Input type="number" min="0" value={formData.min_order_amount} onChange={e => setFormData({ ...formData, min_order_amount: e.target.value })} className="rounded-xl dark:bg-brand-surface dark:border-brand-border dark:text-brand-text" />
            <p className="text-xs text-brand-text dark:text-brand-text">Оставьте 0, если промокод действует при любой сумме.</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="dark:text-brand-text">Тип</Label>
              <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                <SelectTrigger className="rounded-xl dark:bg-brand-surface dark:border-brand-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Процент</SelectItem>
                  <SelectItem value="fixed">Фиксированная сумма</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="dark:text-brand-text">Значение *</Label>
              <Input
                type="number"
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                placeholder={formData.type === 'percent' ? '10' : '500'}
                className="rounded-xl dark:bg-brand-surface dark:border-brand-border dark:text-brand-text"
                required
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="dark:text-brand-text">Лимит использований</Label>
              <Input
                type="number"
                value={formData.limit}
                onChange={(e) => setFormData({ ...formData, limit: e.target.value })}
                className="rounded-xl dark:bg-brand-surface dark:border-brand-border dark:text-brand-text"
              />
            </div>
            <div className="space-y-2">
              <Label className="dark:text-brand-text">Действует до *</Label>
              <Input
                type="date"
                value={formData.expires_at}
                onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                className="rounded-xl dark:bg-brand-surface dark:border-brand-border dark:text-brand-text"
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="rounded-xl dark:border-brand-border">Отмена</Button>
            <Button type="submit" className="bg-brand-accent dark:bg-brand-accent text-brand-text dark:text-brand-text hover:bg-brand-surface dark:hover:bg-brand-surface rounded-xl">Создать</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Coins Modal Component
const CoinsModal = ({ open, onClose, onSave }) => {
  const [email, setEmail] = useState('');
  const [amount, setAmount] = useState('');

  const handleSubmit = (action) => {
    if (!email || !amount) {
      alert('Заполните все поля');
      return;
    }
    onSave(email, amount, action);
    setEmail('');
    setAmount('');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-2xl dark:bg-brand-surface">
        <DialogHeader>
          <DialogTitle className="dark:text-brand-text">Управление coins</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="dark:text-brand-text">Email пользователя</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
              className="rounded-xl dark:bg-brand-surface dark:border-brand-border dark:text-brand-text"
            />
          </div>
          <div className="space-y-2">
            <Label className="dark:text-brand-text">Количество coins</Label>
            <Input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="100"
              className="rounded-xl dark:bg-brand-surface dark:border-brand-border dark:text-brand-text"
            />
          </div>
          <div className="flex gap-2">
            <Button 
              className="flex-1 bg-brand-accent text-brand-text hover:bg-brand-accent rounded-xl"
              onClick={() => handleSubmit('add')}
            >
              Добавить
            </Button>
            <Button 
              className="flex-1 bg-brand-accent text-brand-text hover:bg-brand-accent rounded-xl"
              onClick={() => handleSubmit('subtract')}
            >
              Вычесть
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AdminPage;
